
import { GENDERS } from '@/constants'
import { DepartmentResource, Shift } from '@/models'
import { SettingResource } from '@/models/Setting'
import { UserPermission } from '@/redux/user/userReducer'
import { DateFormat } from '@/types'

import { formatDate } from './date'
import { trans } from './trans'

const isIndexable = (value: unknown): value is Record<string, unknown> => (
    (typeof value === 'object' && value !== null) || typeof value === 'string'
)

// Legacy: reads a nested property by its path (e.g. 'user.person.firstname'), returns the default when a step is missing.
// TypeScript code should use optional chaining instead, which keeps the types.
const getObjProperty = (containerObj: unknown, str: string, defaultValueArg?: unknown): unknown => {
    const defaultValue = typeof defaultValueArg !== 'undefined' ? defaultValueArg : null

    return str.split('.').reduce<unknown>((obj, key) => {
        if (obj === undefined || obj === null) {
            return defaultValue
        }

        return isIndexable(obj) ? obj[key] : undefined
    }, containerObj)
}

const isObjEmpty = (object: object): boolean => Object.keys(object).length === 0

const capitalizeFirstLetter = (string: string): string => string.charAt(0).toUpperCase() + string.slice(1)

const isTouch = (): boolean => 'ontouchstart' in document.documentElement

const getDeviatedTimeShift = (shift: Shift | null | undefined, userId: string | null | undefined, showEndTime: boolean): string | null => {
    if (! shift || ! userId || ! shift.shiftPlannings || ! shift.shiftPlannings.length) {
        return null
    }

    const shiftPlanning = shift.shiftPlannings.filter(a => getObjProperty(a, 'user.id') === userId)[0]

    if (! shiftPlanning) {
        return null
    }
    const deviated = shiftPlanning.start !== shift.start
        || (showEndTime && (shiftPlanning.end !== shift.end))

    if (! deviated) {
        return null
    }

    if (deviated && showEndTime) {
        return `${formatDate(new Date(shiftPlanning.start), DateFormat.timeOnlyLabel)} - ${formatDate(new Date(shiftPlanning.end), DateFormat.timeOnlyLabel)}`
    }

    return `${formatDate(new Date(shiftPlanning.start), DateFormat.timeOnlyLabel)}`
}

// Checks if tab is not active, then starts a function
const hardRefresh = (): void => {
    if (caches) {
        // Service worker cache should be cleared with caches.delete()
        caches.keys().then((names) => {
             
            for (const name of names) caches.delete(name)
        })
    }
    // delete browser cache and hard reload
    window.location.reload()
}

const removeSlashAtEndOfString = (url: string): string => {
    if (url.charAt(url.length - 1) === '/') {
        return url.substr(0, url.length - 1)
    }
    return url
}

const checkPermission = (permissions: UserPermission[], name: string): boolean => !! permissions.find(
    permission => permission.name === name,
)

const shouldShowEndTime = (settings: SettingResource[], departmentId?: string | null): boolean => {
    const showEndTimeSettings = settings.filter(setting => setting.key === 'EMPLOYEE_APP_SHOW_END_TIMES')
    const departmentSettings = showEndTimeSettings.find(setting => setting.department?.id === departmentId)
    if (departmentSettings) {
        return departmentSettings.value === '1'
    }

    const generalSettings = showEndTimeSettings.find(setting => ! setting.department)
    if (generalSettings) {
        return generalSettings.value === '1'
    }

    return true
}

const shouldShowSchedule = (settings: SettingResource[], departments?: DepartmentResource[]): boolean | undefined => {
    const showScheduleSettings = settings.filter(setting => setting.key === 'VIEW_DEPARTMENT_SCHEDULE')
    const departmentIds = departments?.map(department => department.id) ?? []
    const departmentSettings = showScheduleSettings.filter(setting => (
        setting.department ? departmentIds.includes(setting.department.id) : false
    ))
    if (departmentSettings.length) {
        return !! departmentSettings.some(setting => setting.value === '1')
    }

    const generalSettings = showScheduleSettings.find(setting => ! setting.department)
    if (generalSettings) {
        return generalSettings.value === '1'
    }
}

const convertSpacesToDash = (text: string): string => text.replace(/\s+/g, '-').toLowerCase()

const generateGenderOptions = (currentGender?: string | null): { label: string, value: string }[] => [
    ...GENDERS
        .filter(gender => ! (currentGender && gender.value === 'null'))
        .map(gender => ({
            label: trans(`common.${gender.label}`),
            value: gender.value,
        })),
]

const roundNumberDecimals = (value: number): number => Math.round(value * 100) / 100

const roundNumberToRoundedNumberToQuarter = (value: number): number => Math.round(value * 4) / 4

const generateUuid = (): string => crypto.randomUUID()

export {
    capitalizeFirstLetter,
    checkPermission,
    convertSpacesToDash,
    generateGenderOptions,
    generateUuid,
    getDeviatedTimeShift,
    getObjProperty,
    hardRefresh,
    isObjEmpty,
    isTouch,
    removeSlashAtEndOfString,
    roundNumberDecimals,
    roundNumberToRoundedNumberToQuarter,
    shouldShowEndTime,
    shouldShowSchedule,
}
